// Two small charts for the dashboard, drawn with plain elements / SVG — not worth a chart library.
import { palette } from '@fleetspoke/ui/tokens';
import { Box, Link, Stack, Typography, useTheme } from '@mui/material';
import { type KeyboardEvent, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import type { MovementDay } from '../data/dashboard';
import { localeTag } from '../lib/time';

/** Horizontal percentage bars (0–100), each row a link when `to` is given, its detail line under the bar. */
export function PercentBars({
  items,
  empty,
}: {
  items: { key: string; label: string; value: number; detail: string; to?: string }[];
  empty: string;
}) {
  const mode = useTheme().palette.mode;
  if (!items.length) return <Typography color="text.secondary">{empty}</Typography>;
  return (
    <Stack spacing={1.5} data-testid="utilisation-bars">
      {items.map((i) => {
        const row = (
          <>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="body2">{i.label}</Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              >
                {i.value.toFixed(1)} %
              </Typography>
            </Stack>
            <Box
              sx={{
                height: 8,
                my: 0.5,
                bgcolor: palette.neutralSoft[mode],
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.min(100, Math.max(0, i.value))}%`,
                  bgcolor: palette.accent[mode],
                  borderRadius: 1,
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {i.detail}
            </Typography>
          </>
        );
        return i.to ? (
          <Link
            key={i.key}
            component={RouterLink}
            to={i.to}
            underline="none"
            color="inherit"
            sx={{ display: 'block', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
          >
            {row}
          </Link>
        ) : (
          <Box key={i.key}>{row}</Box>
        );
      })}
    </Stack>
  );
}

/** "8 Sept" for a calendar date that is already in the company timezone. */
function dayLabel(day: string, language: string): string {
  return new Intl.DateTimeFormat(localeTag(language), {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
  }).format(new Date(`${day}T00:00:00Z`));
}

/** A grid step of 1, 2 or 5 × 10ⁿ giving about four lines up to `max`. */
export function niceStep(max: number): number {
  const raw = Math.max(1, max) / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow;
  return Math.max(1, step);
}

/** Busiest day by arrivals, or null when nothing arrived. */
export function busiestDay(days: readonly MovementDay[]): MovementDay | null {
  const best = days.reduce<MovementDay | null>(
    (b, d) => (d.arrivals > (b?.arrivals ?? 0) ? d : b),
    null,
  );
  return best;
}

const H = 190;
const PAD = { l: 28, r: 6, t: 10, b: 24 };

/**
 * Arrivals above a baseline, departures below it, one slot per day (CR_20260924_Dashboard_Revamp §3.5). Drawn at the
 * container's pixel width so the text never scales.
 */
export function MovementBars({ days }: { days: readonly MovementDay[] }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const lang = i18n.language;
  const arrivals = days.reduce((n, d) => n + d.arrivals, 0);
  const departures = days.reduce((n, d) => n + d.departures, 0);
  const busiest = busiestDay(days);

  const W = Math.max(280, width || 600);
  const base = PAD.t + (H - PAD.t - PAD.b) * 0.78;
  const up = base - PAD.t;
  const down = H - PAD.b - base;
  const maxA = Math.max(0, ...days.map((d) => d.arrivals));
  const maxD = Math.max(0, ...days.map((d) => d.departures));
  const scale = Math.max(1, maxA, (maxD * up) / down);
  const y = (v: number) => (v / scale) * up;
  const slot = (W - PAD.l - PAD.r) / Math.max(1, days.length);
  const bar = slot * 0.64;
  const step = niceStep(scale);
  const grid: number[] = [];
  for (let v = step; v <= scale; v += step) grid.push(v);
  const last = days.length - 1;
  const ticks = [0, 7, 14, 21].filter((i) => i < last).concat(last >= 0 ? [last] : []);
  const ink2 = theme.palette.text.secondary;
  const line = theme.palette.divider;
  const accent = palette.accent[mode];
  const steel = palette.steel[mode];
  const tip = (i: number) => {
    const d = days[i]!;
    return t('dashboard.movements_tip', {
      date: dayLabel(d.day, lang),
      in: d.arrivals,
      out: d.departures,
    });
  };
  const onKey = (e: KeyboardEvent) => {
    if (!days.length) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const d = e.key === 'ArrowRight' ? 1 : -1;
      setHover((h) => Math.min(last, Math.max(0, (h ?? (d > 0 ? -1 : last + 1)) + d)));
    }
  };
  const total = (label: string, value: string | number, colour?: string) => (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center">
        {colour ? <Box sx={{ width: 9, height: 9, borderRadius: 0.5, bgcolor: colour }} /> : null}
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box data-testid="movements-chart">
      <Stack direction="row" spacing={3} sx={{ mb: 0.75 }} data-testid="movements-totals">
        {total(t('dashboard.arrivals'), arrivals, accent)}
        {total(t('dashboard.departures'), departures, steel)}
        {total(t('dashboard.busiest_day'), busiest ? dayLabel(busiest.day, lang) : '—')}
      </Stack>
      <Box ref={box} sx={{ position: 'relative' }}>
        <svg
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={t('dashboard.movements_aria', { in: arrivals, out: departures })}
          tabIndex={0}
          onKeyDown={onKey}
          onBlur={() => setHover(null)}
          onMouseLeave={() => setHover(null)}
          style={{ display: 'block', overflow: 'visible', outline: 'none' }}
        >
          {grid.map((v) => (
            <g key={v}>
              <line x1={PAD.l} x2={W - PAD.r} y1={base - y(v)} y2={base - y(v)} stroke={line} />
              <text
                x={PAD.l - 6}
                y={base - y(v) + 3.5}
                textAnchor="end"
                fontSize={10.5}
                fill={ink2}
              >
                {v}
              </text>
            </g>
          ))}
          <line x1={PAD.l} x2={W - PAD.r} y1={base} y2={base} stroke={ink2} strokeOpacity={0.5} />
          {days.map((d, i) => {
            const x = PAD.l + i * slot + slot * 0.18;
            return (
              <g key={d.day} opacity={hover === null || hover === i ? 1 : 0.55}>
                {d.arrivals ? (
                  <rect
                    x={x}
                    y={base - y(d.arrivals)}
                    width={bar}
                    height={y(d.arrivals)}
                    rx={2}
                    fill={accent}
                  />
                ) : (
                  <rect x={x} y={base - 1.5} width={bar} height={1.5} fill={line} />
                )}
                {d.departures ? (
                  <rect
                    x={x}
                    y={base + 1}
                    width={bar}
                    height={Math.max(3, y(d.departures))}
                    rx={2}
                    fill={steel}
                  />
                ) : null}
                <rect
                  x={PAD.l + i * slot}
                  y={PAD.t}
                  width={slot}
                  height={H - PAD.b - PAD.t}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                  data-testid={`movements-day-${d.day}`}
                />
              </g>
            );
          })}
          {ticks.map((i) => (
            <text
              key={i}
              x={PAD.l + i * slot + slot / 2}
              y={H - 6}
              textAnchor={i === last ? 'end' : 'middle'}
              fontSize={10.5}
              fill={ink2}
            >
              {i === last ? t('dashboard.today') : dayLabel(days[i]!.day, lang)}
            </text>
          ))}
        </svg>
        {hover !== null && days[hover] ? (
          <Box
            role="status"
            sx={{
              position: 'absolute',
              pointerEvents: 'none',
              left: Math.min(W - 60, Math.max(60, PAD.l + hover * slot + slot / 2)),
              top: Math.max(0, base - y(days[hover].arrivals) - 8),
              transform: 'translate(-50%, -100%)',
              bgcolor: 'text.primary',
              color: 'background.paper',
              fontSize: 12,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              whiteSpace: 'nowrap',
            }}
            data-testid="movements-tip"
          >
            {tip(hover)}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
